/**
 * ZATCA Phase-1 QR payload (TLV, Base64).
 *
 * ── WHAT THIS IS, EXACTLY ────────────────────────────────────────────────────
 *
 * This implements the Phase-1 ("Generation") simplified-invoice QR: five
 * tag-length-value fields, concatenated and Base64-encoded, as required for a
 * simplified tax invoice in Saudi Arabia.
 *
 * IT IS NOT Phase-2 e-invoicing. Specifically NOT implemented here:
 *
 *   • Tag 6 — the invoice XML hash
 *   • Tag 7 — the ECDSA digital signature
 *   • Tag 8 — the signing certificate's public key
 *   • Tag 9 — the certificate/device stamp
 *   • UBL 2.1 XML invoice generation
 *   • Invoice Counter Value (ICV) and previous-invoice-hash chaining
 *   • CSR generation, Compliance CSID, Production CSID onboarding
 *   • Clearance (B2B) or Reporting (B2C) submission to ZATCA
 *
 * Producing tags 6–9 without a real CSID from ZATCA would yield a QR that looks
 * complete and fails actual verification — worse than not producing it. So this
 * stops at the boundary it can honestly implement.
 *
 * Nothing in this product may therefore be described as "ZATCA certified",
 * "ZATCA approved" or "fully compliant". See docs/ZATCA.md.
 *
 * ── DETERMINISM ──────────────────────────────────────────────────────────────
 *
 * The payload is a pure function of the invoice snapshot. The same snapshot
 * always produces byte-identical output — no clock, no randomness, no reading
 * of current settings. That is what lets a historical invoice reprint with the
 * same QR years later.
 */

/** The five Phase-1 tags. */
export const ZATCA_TAGS = {
  SELLER_NAME: 1,
  VAT_NUMBER: 2,
  TIMESTAMP: 3,
  TOTAL_WITH_VAT: 4,
  VAT_TOTAL: 5,
} as const;

export type ZatcaTag = (typeof ZATCA_TAGS)[keyof typeof ZATCA_TAGS];

export interface ZatcaQrInput {
  /** The seller's registered name, exactly as snapshotted onto the invoice. */
  readonly sellerName: string;
  /** 15-digit VAT registration number. */
  readonly vatNumber: string;
  /** ISO-8601 timestamp of issuance, as snapshotted. */
  readonly issuedAt: string;
  /** Total including VAT, formatted to two decimals — e.g. "40.00". */
  readonly totalWithVat: string;
  /** VAT amount, formatted to two decimals — e.g. "5.22". */
  readonly vatTotal: string;
}

export interface DecodedTlvField {
  readonly tag: number;
  readonly length: number;
  readonly value: string;
}

export class ZatcaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ZatcaError";
  }
}

/** A single TLV value may not exceed 255 bytes, because length is one byte. */
const MAX_VALUE_BYTES = 255;

function encodeField(tag: number, value: string): Uint8Array {
  // UTF-8 BYTES, not characters. An Arabic business name is roughly two bytes
  // per character, so a name that is well within 255 characters can still
  // overflow a 255-byte field — measuring characters here would silently
  // truncate Arabic names that pass an English-only test.
  const valueBytes = new TextEncoder().encode(value);

  if (valueBytes.length > MAX_VALUE_BYTES) {
    throw new ZatcaError(
      `TLV value for tag ${tag} is ${valueBytes.length} bytes, which exceeds the ${MAX_VALUE_BYTES}-byte field limit`,
    );
  }

  const field = new Uint8Array(2 + valueBytes.length);
  field[0] = tag;
  field[1] = valueBytes.length;
  field.set(valueBytes, 2);
  return field;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Builds the Base64 TLV payload for a simplified tax invoice.
 *
 * Field order is fixed at 1,2,3,4,5 — the specification defines the sequence,
 * and a reader that expects it will mis-parse a reordered payload.
 */
export function buildZatcaQrPayload(input: ZatcaQrInput): string {
  const sellerName = input.sellerName.trim();
  const vatNumber = input.vatNumber.trim();

  if (!sellerName) throw new ZatcaError("Seller name is required for the QR payload");
  if (!vatNumber) throw new ZatcaError("VAT registration number is required for the QR payload");
  if (!input.issuedAt) throw new ZatcaError("Issue timestamp is required for the QR payload");

  const fields = [
    encodeField(ZATCA_TAGS.SELLER_NAME, sellerName),
    encodeField(ZATCA_TAGS.VAT_NUMBER, vatNumber),
    encodeField(ZATCA_TAGS.TIMESTAMP, input.issuedAt),
    encodeField(ZATCA_TAGS.TOTAL_WITH_VAT, input.totalWithVat),
    encodeField(ZATCA_TAGS.VAT_TOTAL, input.vatTotal),
  ];

  const total = fields.reduce((sum, f) => sum + f.length, 0);
  const payload = new Uint8Array(total);
  let offset = 0;
  for (const field of fields) {
    payload.set(field, offset);
    offset += field.length;
  }

  return bytesToBase64(payload);
}

/**
 * Decodes a Base64 TLV payload back into its fields.
 *
 * This exists so tests can VERIFY a payload by reading it, rather than by
 * comparing against a hardcoded string that was itself produced by the encoder
 * — a comparison which passes just as happily when both are wrong.
 */
export function decodeZatcaQrPayload(base64: string): DecodedTlvField[] {
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(base64);
  } catch {
    throw new ZatcaError("Payload is not valid Base64");
  }

  const decoder = new TextDecoder();
  const fields: DecodedTlvField[] = [];
  let offset = 0;

  while (offset < bytes.length) {
    if (offset + 2 > bytes.length) {
      throw new ZatcaError(`Truncated TLV header at byte ${offset}`);
    }

    const tag = bytes[offset]!;
    const length = bytes[offset + 1]!;
    const start = offset + 2;
    const end = start + length;

    if (end > bytes.length) {
      throw new ZatcaError(`TLV field for tag ${tag} claims ${length} bytes but the payload ends early`);
    }

    fields.push({ tag, length, value: decoder.decode(bytes.subarray(start, end)) });
    offset = end;
  }

  return fields;
}

/** Convenience: decode into a tag-keyed map. */
export function decodeZatcaQrPayloadAsMap(base64: string): Map<number, string> {
  return new Map(decodeZatcaQrPayload(base64).map((f) => [f.tag, f.value]));
}

/**
 * The number of bytes a value will occupy, so callers can warn BEFORE issuing
 * an invoice that a business name is too long to encode.
 */
export function tlvValueByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

export const ZATCA_MAX_VALUE_BYTES = MAX_VALUE_BYTES;

/**
 * What this implementation covers, in a form the UI and docs can render so the
 * claim shown to a user always matches the code.
 */
export const ZATCA_IMPLEMENTATION_STATUS = {
  phase1QrGenerated: true,
  tagsImplemented: [1, 2, 3, 4, 5] as readonly number[],
  tagsNotImplemented: [6, 7, 8, 9] as readonly number[],
  xmlUblGenerated: false,
  invoiceHashChaining: false,
  cryptographicStamp: false,
  csidOnboarding: false,
  clearanceOrReporting: false,
  /** Deliberately not a compliance claim. */
  statementEn:
    "Phase-1 simplified-invoice QR generation only. Not integrated with ZATCA; not certified or approved.",
  statementAr:
    "يقتصر التنفيذ على توليد رمز QR للفاتورة المبسطة (المرحلة الأولى). غير مرتبط بهيئة الزكاة والضريبة والجمارك، وغير معتمد.",
} as const;
